import * as React from 'react';
import { css } from 'styled-components';
import { UitkButtonSize } from 'uitk-react-button';

// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Components
import Layout from 'src/components/common/theme/Layout';
import { Typo } from 'src/components/common/ui';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import { checkValue, LightenDarkenColor } from 'src/components/common/helper';
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';
import Figure, { FigureProps } from 'src/components/common/ui/figure/figure';

import { getDeviceImage } from 'src/components/common/theme/component';

// Css Style Import
import(/* webpackChunkName: "v1-inspiration-styles" */ 'src/components/common/modules/v1/inspiration/styles/inspiration.css');

const placeholder = 'https://mesocp3.azureedge.net/media/c32fmcus/static-img.jpg';

const Inspiration: React.FC = () => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: { data: { data, id }, key, ...rest },
  } = useComponentContext();

  const {
    headline,
    subHeadline,
    description,
    cTAText,
    cTATextColor,
    cTAColor,
    cTAUrl,
    openUrlInNewTab,
    imageLeft,
    imageRight,
    turnOffGradient,
    moduleTextColor,
    moduleTextStyle,
    backgroundColor,
    opacitySelect,
    moduleStyle,
    imageLeftLogo1,
    imageLeftLogo2
  } = data;

  const { width: windowWidth } = useWindowDimensions();

  let windowSize = '';
  if (windowWidth > 920) windowSize = 'desktop';
  else if (windowWidth > 767) windowSize = 'tablet';
  else if (windowWidth > 500 && windowWidth < 768) windowSize = 'medium-device';
  else windowSize = 'mobile'; 

  // Dynamic style overrides
  const moduleCss = () => {
    return `
      ${
        backgroundColor
          ? `
          background-color: ${backgroundColor};
        `
          : ''
      }
      ${
        moduleTextColor
          ? `
          .main-title {
            color: ${moduleTextColor} !important;
          }
          .sub-title {
            color: ${moduleTextColor};
          }
          .main-content {
            color: ${moduleTextColor} !important;
          }
        `
          : ''
      }
      ${
        cTATextColor
          ? `
         .inspiration-content-button-container button {
            color: ${cTATextColor};
         } 
        `
          : ''
      }
      ${
        cTAColor
          ? `
         .inspiration-content-button-container button {
            background-color: ${cTAColor} !important;
         }
         .inspiration-content-button-container button:hover {
            background-color: ${LightenDarkenColor(cTAColor, -0.1)} !important;
          } 
        `
          : ''
      }
      ${
        opacitySelect
          ? `
         .inspiration-overlay {
            opacity: ${opacitySelect / 100};
         } 
        `
          : ''
      }
      .inspiration-left-side {
        background-image: url(${checkValue(imageLeft, true) ? getDeviceImage(imageLeft, windowSize) : placeholder});
      }
      .inspiration-right-side {
        background-image: url(${checkValue(imageRight, true) ? getDeviceImage(imageRight, windowSize) : placeholder});
      }
    `;
  };

  const primaryButtonProps: PrimaryButtonProps = {
    text: cTAText,
    tag: 'button',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'inspiration',
      eventLabel: cTAText,
    },
    url: cTAUrl,
    target: checkValue(openUrlInNewTab) && openUrlInNewTab.toLowerCase() === 'true' ? '_blank' : '_self',
    size: UitkButtonSize.LARGE,
    exClass: 'inspiration-content-cta',
  };

  const content = () => (
    <>
      {checkValue(turnOffGradient) && turnOffGradient.toLowerCase() === 'true' ? null : <div className={`inspiration-overlay ${moduleTextStyle && moduleTextStyle.toLowerCase() === 'text on right image' ? 'right' : 'left'}`} />}
      <div className="inspiration-content">
        {headline ? (
          <Typo.HTMLText tag="h2" exClass="main-title override-heading-font-family">
            {headline}
          </Typo.HTMLText>
        ) : null}
        {subHeadline ? (
          <Typo.HTMLText tag="h3" exClass="sub-title override-heading-font-family">
            {subHeadline}
          </Typo.HTMLText>
        ) : null}
        {description ? (
          <Typo.HTMLText tag="p" exClass="main-content">
            {description}
          </Typo.HTMLText>
        ) : null}
        {cTAText && cTAUrl ? (
          <div className="inspiration-content-button-container">
            <PrimaryButton {...{ ...primaryButtonProps }} />
          </div>
        ) : null}
      </div>
    </>
  );

  const figurePropsLeftLogo1: FigureProps = {
    src: getDeviceImage(imageLeftLogo1, 'desktop'),
    exClass: 'lozad lozad  acc-image',
    alt: '',
    roundcorner: false,
  };
  const figurePropsLeftLogo2: FigureProps = {
    src: getDeviceImage(imageLeftLogo2, 'desktop'),
    exClass: 'lozad lozad  acc-image',
    alt: '',
    roundcorner: false,
  };

  const logoContent = () => (
    <>
      {checkValue(turnOffGradient) && turnOffGradient.toLowerCase() === 'true' ? null : <div className={`inspiration-overlay ${moduleTextStyle && moduleTextStyle.toLowerCase() === 'text on right image' ? 'right' : 'left'}`} />}
    
      <div className="logo-container">
        <div className="logo-wrapper">
        {checkValue(imageLeftLogo1, true) ? 
          <div className="logo-section-left">
            <Figure {...figurePropsLeftLogo1} />
          </div> : <></> }
        {checkValue(imageLeftLogo2, true) ? 
          <div className="logo-section-right">
            <Figure {...figurePropsLeftLogo2} />
          </div> : <></> }
        </div>
      </div>
    </>
  );

  return (
    <Layout
      css={css`
        ${moduleCss()}
      `}
      className="inspiration"
      HtmlTag="section"
      id={id}     
    >
      <div className={`container ${checkValue(moduleStyle) ? moduleStyle.toLowerCase() : ''}`}>
        <div className="inspiration-wrapper">
          { moduleTextStyle && moduleTextStyle.toLowerCase() === 'text on right image' ? 
            <>
              <div className="inspiration-left-side inspiration-fix-height">{logoContent()}</div>
              <div className="inspiration-right-side">{content()}</div>
            </> :
            <>
              <div className="inspiration-left-side">{content()}</div>
              <div className="inspiration-right-side inspiration-fix-height">{logoContent()}</div>
            </>
          }
        </div>
      </div>
    </Layout>
  );
};

export default Inspiration;
